import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Loader2, DollarSign } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { ventasService } from '@/services/ventas.service';
import { customerPaymentsService } from '@/services/customer-payments.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';

const formatCurrency = (val: any) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(val) || 0);

export function ReceivablesPage() {
  const { user } = useAuthStore();
  const [sales, setSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();

  const loadReceivables = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      // Get sales with PENDING_PAYMENT status
      const result = await ventasService.findAll({ companyId: user.company.id, status: 'PENDING_PAYMENT', search: search || undefined });
      const pendingSales = (result.data || []).filter((sale: any) => {
        const total = Number(sale.grandTotal ?? sale.total ?? 0);
        const paid = Number(sale.paidAmount ?? 0);
        return total - paid > 0.009;
      });
      setSales(pendingSales);
    } catch (err: any) {
      handleApiError(err);
    }
    setLoading(false);
  }, [user, search]);

  useEffect(() => {
    loadReceivables();
  }, [loadReceivables]);

  // Group sales by customer
  const customersMap = new Map();
  sales.forEach(sale => {
    if (!sale.customer) return;
    const cid = sale.customer.id;
    if (!customersMap.has(cid)) {
      customersMap.set(cid, {
        customer: sale.customer,
        totalDebt: 0,
        salesCount: 0,
        sales: []
      });
    }
    const data = customersMap.get(cid);
    const debt = Number(sale.grandTotal) - Number(sale.paidAmount);
    data.totalDebt += debt;
    data.salesCount += 1;
    data.sales.push(sale);
  });

  const groupedCustomers = Array.from(customersMap.values());

  const handleOpenPayment = (customerData: any) => {
    setSelectedCustomer(customerData);
    setPaymentAmount(customerData.totalDebt.toString());
    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.company?.id || !selectedCustomer || !paymentAmount) return;
    
    setSaving(true);
    try {
      await customerPaymentsService.create({
        companyId: user.company.id,
        customerId: selectedCustomer.customer.id,
        amount: Number(paymentAmount),
        paymentMethod: 'CASH', // default for now
        notes: 'Abono registrado desde Cuentas por Cobrar'
      });
      
      setShowPaymentModal(false);
      dialogs.alert('Abono registrado correctamente', 'Éxito');
      loadReceivables();
    } catch (err: any) {
      handleApiError(err);
    }
    setSaving(false);
  };

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadReceivables} onLogin={handleAuthRedirect} />
      
      {!error && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Cuentas por Cobrar</h1>
              <p className="text-sm text-ios-gray mt-1">
                {groupedCustomers.length} clientes con saldos pendientes
              </p>
            </div>
          </div>

          <div className="relative max-w-sm mb-6">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar folio o cliente..."
              className="ios-input w-full pl-9 h-9 text-sm"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 size={24} className="animate-spin text-ios-gray" />
            </div>
          ) : groupedCustomers.length === 0 ? (
            <div className="ios-card p-12 text-center text-ios-gray flex flex-col items-center">
              <DollarSign size={48} className="mb-4 opacity-20" />
              <p className="text-lg font-medium text-ios-dark">Todo al corriente</p>
              <p className="text-sm mt-1">No hay cuentas por cobrar pendientes.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedCustomers.map((data: any) => (
                <div key={data.customer.id} className="ios-card p-5">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-ios-dark">{data.customer.name}</h3>
                      <p className="text-sm text-ios-gray">
                        {data.salesCount} {data.salesCount === 1 ? 'ticket pendiente' : 'tickets pendientes'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-ios-gray mb-1">Deuda Total</p>
                      <p className="text-lg font-bold text-ios-error">
                        {formatCurrency(data.totalDebt)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    {data.sales.slice(0, 3).map((sale: any) => (
                      <div key={sale.id} className="flex justify-between text-sm py-1 border-b border-ios-gray/10 last:border-0">
                        <span className="text-ios-gray">Folio: {sale.folio}</span>
                        <span className="font-medium text-ios-dark">
                          {formatCurrency(Number(sale.grandTotal) - Number(sale.paidAmount))}
                        </span>
                      </div>
                    ))}
                    {data.sales.length > 3 && (
                      <p className="text-xs text-ios-gray text-center pt-2">
                        + {data.sales.length - 3} tickets más...
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handleOpenPayment(data)}
                    className="ios-button w-full justify-center text-sm font-semibold"
                  >
                    Registrar Abono
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Payment Modal */}
          {showPaymentModal && selectedCustomer && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <div className="ios-card w-full max-w-md p-6 animate-ios-slide-up">
                <h2 className="text-xl font-bold mb-4">Abonar a Cuenta</h2>
                
                <div className="mb-6 p-4 bg-ios-gray/5 rounded-xl">
                  <p className="text-sm text-ios-gray mb-1">Cliente</p>
                  <p className="font-medium">{selectedCustomer.customer.name}</p>
                  <div className="mt-2 pt-2 border-t border-ios-gray/20 flex justify-between">
                    <span className="text-sm text-ios-gray">Deuda Actual:</span>
                    <span className="font-bold text-ios-error">{formatCurrency(selectedCustomer.totalDebt)}</span>
                  </div>
                </div>

                <form onSubmit={handleSubmitPayment}>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-ios-dark mb-1">
                      Monto a Abonar
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray">$</span>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        max={selectedCustomer.totalDebt}
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        className="ios-input w-full pl-7"
                        required
                        autoFocus
                      />
                    </div>
                    <p className="text-xs text-ios-gray mt-2">
                      El sistema aplicará este abono automáticamente a los tickets más antiguos.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowPaymentModal(false)}
                      className="ios-button-outline flex-1"
                      disabled={saving}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="ios-button flex-1 justify-center"
                      disabled={saving || !paymentAmount || Number(paymentAmount) <= 0}
                    >
                      {saving ? <Loader2 size={18} className="animate-spin" /> : 'Confirmar Abono'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
